import { useEffect, useState, type FormEvent } from "react";
import {
  TRANSICIONES_HONORARIO,
  type CuotaHonorario,
  type EstadoHonorario,
  type FrecuenciaCuotas,
  type Honorario,
  type ModalidadHonorario,
  type Moneda,
  type MovimientoEconomico,
  type Rendicion,
  type TipoDescuentoHonorario,
  type TipoMovimientoEconomico,
  type Usuario,
} from "../../../shared/types";
import { AlternativaBinaria } from "../../components/AlternativaBinaria";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { SelectorCategoriaEconomica } from "../../components/SelectorCategoriaEconomica";
import { SelectorTipoHonorario } from "../../components/SelectorTipoHonorario";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { NATURALEZA_CATEGORIA } from "../../lib/economia";
import { PresupuestadorHonorarios } from "../economia/PresupuestadorHonorarios";

const FRECUENCIAS_CUOTAS: FrecuenciaCuotas[] = ["semanal", "quincenal", "mensual"];
// Campos que el backend rechaza modificar si el honorario ya tiene cobros
// registrados (ver CAMPOS_PROTEGIDOS_POR_COBRO en functions/_app/routes/honorarios.ts).
const CAMPOS_PROTEGIDOS_POR_COBRO = [
  "modalidad",
  "porcentaje",
  "importe_fijo",
  "base_calculo",
  "honorario_estimado",
  "honorario_pactado",
  "honorario_regulado",
  "moneda",
  "descuento_tipo",
  "descuento_valor",
  "motivo_descuento",
] as const;

// Ver decisión 2 del plan: qué monto se muestra depende del `estado`, no de
// la modalidad ni del tipo. Cobrado/Anulado usan la misma precedencia que ya
// usa la tabla (regulado ?? pactado ?? estimado) para decidir cuál mostrar.
function campoMontoActivo(h: HonorarioConCalculo | null): "honorario_estimado" | "honorario_pactado" | "honorario_regulado" {
  const estado = h?.estado ?? "Estimado";
  if (estado === "Pactado") return "honorario_pactado";
  if (estado === "Regulado") return "honorario_regulado";
  if (estado === "Cobrado" || estado === "Anulado") {
    if (h?.honorario_regulado != null) return "honorario_regulado";
    if (h?.honorario_pactado != null) return "honorario_pactado";
    return "honorario_estimado";
  }
  return "honorario_estimado";
}

function mostrarFechasPorEstado(estado: EstadoHonorario): boolean {
  return estado !== "Pactado" && estado !== "Regulado" && estado !== "Cobrado";
}

const LABEL_MONTO_ACTIVO: Record<"honorario_estimado" | "honorario_pactado" | "honorario_regulado", string> = {
  honorario_estimado: "Valor estimado",
  honorario_pactado: "Monto pactado",
  honorario_regulado: "Monto regulado",
};

type HonorarioConCalculo = Honorario & { cobrado: number; saldoPendiente: number | null };

const MODALIDADES: ModalidadHonorario[] = [
  "Porcentaje",
  "Monto Fijo",
  "Mixto",
  "Regulado Judicialmente",
  "Extrajudicial",
];
// Opciones que se ofrecen en el <select> de estado de un honorario: el
// estado actual (para que siga mostrándose seleccionado) + únicamente las
// transiciones que TRANSICIONES_HONORARIO (shared/types.ts, la misma
// fuente que ya usa el backend en lib/honorarios/estados.ts) permite desde
// ahí. Antes se listaban los 9 estados siempre, incluso los que el
// backend iba a rechazar con TRANSICION_INVALIDA.
function opcionesEstadoDisponibles(actual: EstadoHonorario): EstadoHonorario[] {
  return [actual, ...TRANSICIONES_HONORARIO[actual]];
}
const MONEDAS: Moneda[] = ["ARS", "USD"];
const TIPOS_MOVIMIENTO: TipoMovimientoEconomico[] = [
  "Capital del Cliente",
  "Honorarios Pactados",
  "Honorarios Regulados",
  "Honorarios Cobrados",
  "Gasto Adelantado",
  "Gasto Recuperado",
  "Pago Recibido",
  "Pago al Cliente",
  "Pago a Profesional",
  "Aporte",
  "Retención",
  "Otro Ingreso",
  "Otro Egreso",
];
// "Pago Recibido" queda fuera de la carga manual: no está vinculado a
// ningún saldo y ninguna ruta del backend lo genera — ver la misma nota en
// EconomiaPage.tsx (auditoría de integridad).
const TIPOS_MOVIMIENTO_SELECCIONABLES = TIPOS_MOVIMIENTO.filter((t) => t !== "Pago Recibido");

export function PestanaEconomico({
  expedienteId,
  clienteId,
  honorarios,
  rendiciones,
  movimientos,
  onCambio,
}: {
  expedienteId: string;
  clienteId: string;
  honorarios: HonorarioConCalculo[];
  rendiciones: Rendicion[];
  movimientos: MovimientoEconomico[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();

  // --- Honorarios ---
  const [modalHonorarioAbierto, setModalHonorarioAbierto] = useState(false);
  const [presupuestadorAbierto, setPresupuestadorAbierto] = useState(false);
  const [enEdicionHonorario, setEnEdicionHonorario] = useState<HonorarioConCalculo | null>(null);
  const [cuotasHonorarioId, setCuotasHonorarioId] = useState<string | null>(null);
  const [cuotas, setCuotas] = useState<CuotaHonorario[]>([]);
  const [cargandoCuotas, setCargandoCuotas] = useState(false);
  const [editarCuotasAbierto, setEditarCuotasAbierto] = useState(false);
  const [guardandoCuotas, setGuardandoCuotas] = useState(false);
  const [planCuotas, setPlanCuotas] = useState({
    anticipo: "0",
    cantidadCuotas: "1",
    frecuencia: "mensual" as FrecuenciaCuotas,
    primeraFecha: new Date().toISOString().slice(0, 10),
    interesPctPorCuota: "0",
  });
  const [guardandoHonorario, setGuardandoHonorario] = useState(false);
  // "Anulado" es terminal (no tiene transición de salida) y puede alcanzarse
  // incluso desde "Cobrado" — antes cambiaba con un solo click accidental
  // del <Select>. Ahora ese click sólo abre esta confirmación; el PATCH
  // real recién se dispara al confirmar (ver ConfirmacionDialog más abajo).
  const [confirmandoAnulacionHonorario, setConfirmandoAnulacionHonorario] = useState<HonorarioConCalculo | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const valoresHonorarioIniciales = {
    tipo_honorario: "",
    modalidad: "Porcentaje" as ModalidadHonorario,
    porcentaje: "",
    importe_fijo: "",
    base_calculo: "",
    honorario_estimado: "",
    honorario_pactado: "",
    honorario_regulado: "",
    moneda: "ARS" as Moneda,
    profesional_beneficiario_id: "",
    observaciones: "",
    descuento_tipo: "" as "" | TipoDescuentoHonorario,
    descuento_valor: "",
    motivo_descuento: "",
    fecha_envio: "",
    validez_hasta: "",
  };
  const [valoresHonorario, setValoresHonorario] = useState(valoresHonorarioIniciales);
  // Estado de solo lectura en este modal (se cambia exclusivamente por el
  // <Select> inline de la tabla + PATCH /estado, ver decisión 3 del plan) —
  // se lee al abrir para decidir qué monto/fechas mostrar.
  const [estadoActivo, setEstadoActivo] = useState<EstadoHonorario>("Estimado");
  const [campoActivo, setCampoActivo] = useState<"honorario_estimado" | "honorario_pactado" | "honorario_regulado">(
    "honorario_estimado",
  );
  // Calculadora auxiliar de JUS (bajo modalidad Monto Fijo) — cantidadJus y
  // valorJusVigente son 100% de exhibición en el cliente, nunca lógica de
  // Ley 9.131 (ver decisión 1 del plan).
  const [cantidadJus, setCantidadJus] = useState("");
  const [valorJusVigente, setValorJusVigente] = useState<number | null>(null);

  useEffect(() => {
    // Algunos roles no tienen usuarios.ver por defecto — si falla, el
    // selector de profesional beneficiario degrada a mostrar sólo el id.
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!modalHonorarioAbierto || valoresHonorario.modalidad !== "Monto Fijo") return;
    api
      .get<{ item: { valor: number } | null }>("/catalogos/valor-jus")
      .then((d) => setValorJusVigente(d.item?.valor ?? null))
      .catch(() => setValorJusVigente(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalHonorarioAbierto, valoresHonorario.modalidad]);

  function abrirNuevoHonorario() {
    setEnEdicionHonorario(null);
    setValoresHonorario(valoresHonorarioIniciales);
    setEstadoActivo("Estimado");
    setCampoActivo("honorario_estimado");
    setCantidadJus("");
    setValorJusVigente(null);
    setModalHonorarioAbierto(true);
  }

  function abrirEdicionHonorario(h: HonorarioConCalculo) {
    setEnEdicionHonorario(h);
    setValoresHonorario({
      tipo_honorario: h.tipo_honorario ?? "",
      modalidad: h.modalidad,
      porcentaje: h.porcentaje != null ? String(h.porcentaje) : "",
      importe_fijo: h.importe_fijo != null ? String(h.importe_fijo) : "",
      base_calculo: h.base_calculo ?? "",
      honorario_estimado: h.honorario_estimado != null ? String(h.honorario_estimado) : "",
      honorario_pactado: h.honorario_pactado != null ? String(h.honorario_pactado) : "",
      honorario_regulado: h.honorario_regulado != null ? String(h.honorario_regulado) : "",
      moneda: h.moneda,
      profesional_beneficiario_id: h.profesional_beneficiario_id ?? "",
      observaciones: h.observaciones ?? "",
      descuento_tipo: h.descuento_tipo ?? "",
      descuento_valor: h.descuento_valor != null ? String(h.descuento_valor) : "",
      motivo_descuento: h.motivo_descuento ?? "",
      fecha_envio: h.fecha_envio ?? "",
      validez_hasta: h.validez_hasta ?? "",
    });
    setEstadoActivo(h.estado);
    setCampoActivo(campoMontoActivo(h));
    setCantidadJus("");
    setValorJusVigente(null);
    setModalHonorarioAbierto(true);
  }

  const tieneCobro = Boolean(enEdicionHonorario && enEdicionHonorario.cobrado > 0);
  function campoProtegido(campo: (typeof CAMPOS_PROTEGIDOS_POR_COBRO)[number]) {
    return tieneCobro && (CAMPOS_PROTEGIDOS_POR_COBRO as readonly string[]).includes(campo);
  }

  const montoActivoNum = Number(valoresHonorario[campoActivo] || 0);
  const montoDescuentoLocal = !valoresHonorario.descuento_tipo
    ? 0
    : valoresHonorario.descuento_tipo === "Porcentaje"
      ? montoActivoNum * (Number(valoresHonorario.descuento_valor || 0) / 100)
      : Number(valoresHonorario.descuento_valor || 0);
  const totalFinalLocal = montoActivoNum - montoDescuentoLocal;
  const descuentoSuperaVeinte = montoActivoNum > 0 && montoDescuentoLocal / montoActivoNum > 0.2;
  const totalPorcentajeCalculado =
    valoresHonorario.modalidad === "Porcentaje" && valoresHonorario.base_calculo && valoresHonorario.porcentaje
      ? Math.round(Number(valoresHonorario.base_calculo) * (Number(valoresHonorario.porcentaje) / 100) * 100) / 100
      : null;
  // Monto Fijo define el importe una sola vez (importe_fijo), pero el monto
  // que se muestra en la tabla (Vigente/Saldo Pendiente) sale de un campo
  // snapshot aparte (honorario_estimado/pactado/regulado, ver
  // campoMontoActivo) — sin este atajo, cargar sólo "Importe fijo" deja esos
  // dos totales en "—" aunque el usuario ya escribió el monto una vez.
  const totalMontoFijoCalculado =
    valoresHonorario.modalidad === "Monto Fijo" && valoresHonorario.importe_fijo
      ? Number(valoresHonorario.importe_fijo)
      : null;
  const totalJusCalculado =
    cantidadJus && valorJusVigente != null ? Math.round(Number(cantidadJus) * valorJusVigente * 100) / 100 : null;

  const errorPorcentaje =
    valoresHonorario.modalidad === "Porcentaje" && !valoresHonorario.porcentaje
      ? "El porcentaje es obligatorio para esta modalidad."
      : undefined;
  const errorImporteFijo =
    valoresHonorario.modalidad === "Monto Fijo" && !valoresHonorario.importe_fijo
      ? "El importe fijo es obligatorio para esta modalidad."
      : undefined;
  const errorDescuentoMonto =
    valoresHonorario.descuento_tipo && montoDescuentoLocal > montoActivoNum
      ? "El descuento no puede superar el monto."
      : undefined;
  const errorMotivoDescuento =
    valoresHonorario.descuento_tipo && descuentoSuperaVeinte && !valoresHonorario.motivo_descuento.trim()
      ? "Un descuento superior al 20% requiere un motivo."
      : undefined;
  const hayErroresHonorario = Boolean(errorPorcentaje || errorImporteFijo || errorDescuentoMonto || errorMotivoDescuento);

  function aplicarBaseCalculo() {
    if (totalPorcentajeCalculado == null) return;
    setValoresHonorario((v) => ({ ...v, [campoActivo]: String(totalPorcentajeCalculado) }));
  }

  function aplicarMontoFijoComoActivo() {
    if (totalMontoFijoCalculado == null) return;
    setValoresHonorario((v) => ({ ...v, [campoActivo]: String(totalMontoFijoCalculado) }));
  }

  function aplicarCalculoJus() {
    if (totalJusCalculado == null) return;
    setValoresHonorario((v) => ({ ...v, importe_fijo: String(totalJusCalculado) }));
  }

  async function guardarHonorario(e: FormEvent) {
    e.preventDefault();
    setGuardandoHonorario(true);
    try {
      const cuerpo = {
        tipo_honorario: valoresHonorario.tipo_honorario || null,
        modalidad: valoresHonorario.modalidad,
        porcentaje: valoresHonorario.porcentaje ? Number(valoresHonorario.porcentaje) : null,
        importe_fijo: valoresHonorario.importe_fijo ? Number(valoresHonorario.importe_fijo) : null,
        base_calculo: valoresHonorario.base_calculo || null,
        honorario_estimado: valoresHonorario.honorario_estimado ? Number(valoresHonorario.honorario_estimado) : null,
        honorario_pactado: valoresHonorario.honorario_pactado ? Number(valoresHonorario.honorario_pactado) : null,
        honorario_regulado: valoresHonorario.honorario_regulado ? Number(valoresHonorario.honorario_regulado) : null,
        moneda: valoresHonorario.moneda,
        profesional_beneficiario_id: valoresHonorario.profesional_beneficiario_id || null,
        observaciones: valoresHonorario.observaciones || null,
        descuento_tipo: valoresHonorario.descuento_tipo || null,
        descuento_valor: valoresHonorario.descuento_valor ? Number(valoresHonorario.descuento_valor) : null,
        motivo_descuento: valoresHonorario.motivo_descuento || null,
        fecha_envio: valoresHonorario.fecha_envio || null,
        validez_hasta: valoresHonorario.validez_hasta || null,
        jus_aplicados: cantidadJus ? Number(cantidadJus) : (enEdicionHonorario?.jus_aplicados ?? null),
        valor_jus_usado:
          cantidadJus && valorJusVigente != null ? valorJusVigente : (enEdicionHonorario?.valor_jus_usado ?? null),
      };
      if (enEdicionHonorario) {
        await api.put(`/honorarios/${enEdicionHonorario.id}`, cuerpo);
      } else {
        await api.post("/honorarios", { ...cuerpo, expediente_id: expedienteId });
      }
      mostrar(enEdicionHonorario ? "Honorario actualizado." : "Honorario registrado.", "exito");
      setModalHonorarioAbierto(false);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el honorario.", "error");
    } finally {
      setGuardandoHonorario(false);
    }
  }

  async function cambiarEstadoHonorario(h: HonorarioConCalculo, estado: EstadoHonorario) {
    try {
      await api.patch(`/honorarios/${h.id}/estado`, { estado });
      mostrar("Honorario actualizado.", "exito");
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  async function verCuotas(honorarioId: string) {
    if (cuotasHonorarioId === honorarioId) {
      setCuotasHonorarioId(null);
      return;
    }
    setCuotasHonorarioId(honorarioId);
    setCargandoCuotas(true);
    try {
      const datos = await api.get<{ items: CuotaHonorario[] }>(`/honorarios/${honorarioId}/cuotas`);
      setCuotas(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar las cuotas.", "error");
    } finally {
      setCargandoCuotas(false);
    }
  }

  const [cobroLibreHonorario, setCobroLibreHonorario] = useState<HonorarioConCalculo | null>(null);
  const [valoresCobroLibre, setValoresCobroLibre] = useState({ importe: "", fecha: "", concepto: "" });
  const [guardandoCobroLibre, setGuardandoCobroLibre] = useState(false);

  function abrirCobroLibre(h: HonorarioConCalculo) {
    setCobroLibreHonorario(h);
    setValoresCobroLibre({
      importe: h.saldoPendiente != null ? String(h.saldoPendiente) : "",
      fecha: new Date().toISOString().slice(0, 10),
      concepto: "",
    });
  }

  async function guardarCobroLibre(e: FormEvent) {
    e.preventDefault();
    if (!cobroLibreHonorario) return;
    setGuardandoCobroLibre(true);
    try {
      await api.post(`/honorarios/${cobroLibreHonorario.id}/cobrar`, {
        importe: Number(valoresCobroLibre.importe),
        fecha: valoresCobroLibre.fecha,
        concepto: valoresCobroLibre.concepto || null,
      });
      mostrar("Cobro registrado.", "exito");
      setCobroLibreHonorario(null);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo registrar el cobro.", "error");
    } finally {
      setGuardandoCobroLibre(false);
    }
  }

  async function cobrarCuota(honorarioId: string, cuota: CuotaHonorario) {
    try {
      await api.post(`/honorarios/${honorarioId}/cobrar`, {
        cuota_id: cuota.id,
        fecha: new Date().toISOString().slice(0, 10),
        importe: cuota.importe_total,
      });
      mostrar("Cuota cobrada.", "exito");
      const datos = await api.get<{ items: CuotaHonorario[] }>(`/honorarios/${honorarioId}/cuotas`);
      setCuotas(datos.items);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cobrar la cuota.", "error");
    }
  }

  function abrirEditarCuotas() {
    setPlanCuotas({
      anticipo: "0",
      cantidadCuotas: String(cuotas.length || 1),
      frecuencia: "mensual",
      primeraFecha: new Date().toISOString().slice(0, 10),
      interesPctPorCuota: "0",
    });
    setEditarCuotasAbierto(true);
  }

  async function guardarPlanCuotas(e: FormEvent) {
    e.preventDefault();
    if (!cuotasHonorarioId) return;
    setGuardandoCuotas(true);
    try {
      await api.put(`/honorarios/${cuotasHonorarioId}/cuotas`, {
        anticipo: Number(planCuotas.anticipo) || 0,
        cantidadCuotas: Number(planCuotas.cantidadCuotas) || 1,
        frecuencia: planCuotas.frecuencia,
        primeraFecha: planCuotas.primeraFecha,
        interesPctPorCuota: Number(planCuotas.interesPctPorCuota) || 0,
      });
      mostrar("Plan de cuotas actualizado.", "exito");
      setEditarCuotasAbierto(false);
      const datos = await api.get<{ items: CuotaHonorario[] }>(`/honorarios/${cuotasHonorarioId}/cuotas`);
      setCuotas(datos.items);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el plan de cuotas.", "error");
    } finally {
      setGuardandoCuotas(false);
    }
  }

  const columnasHonorarios: ColumnaTabla<HonorarioConCalculo>[] = [
    { encabezado: "Tipo", render: (h) => h.tipo_honorario ?? "—" },
    { encabezado: "Modalidad", render: (h) => h.modalidad },
    {
      encabezado: "Profesional",
      render: (h) =>
        usuarios.find((u) => u.id === h.profesional_beneficiario_id)?.nombre_completo ?? "—",
    },
    {
      encabezado: "Vigente",
      render: (h) => {
        const vigente = h.honorario_regulado ?? h.honorario_pactado ?? h.honorario_estimado;
        return vigente != null ? `$${vigente.toLocaleString("es-AR")} ${h.moneda}` : "—";
      },
    },
    // Mismo criterio de formateo que "Vigente" arriba: monto + sufijo de
    // moneda (h.moneda) — las tres columnas describen el mismo honorario,
    // en la misma moneda, y deben mostrarla siempre, no sólo la primera.
    { encabezado: "Cobrado", render: (h) => `$${h.cobrado.toLocaleString("es-AR")} ${h.moneda}` },
    {
      encabezado: "Saldo Pendiente",
      render: (h) => (h.saldoPendiente != null ? `$${h.saldoPendiente.toLocaleString("es-AR")} ${h.moneda}` : "—"),
    },
    {
      encabezado: "Estado",
      render: (h) => (
        <Select
          value={h.estado}
          onChange={(e) => {
            const nuevoEstado = e.target.value as EstadoHonorario;
            if (nuevoEstado === "Anulado") {
              setConfirmandoAnulacionHonorario(h);
              return;
            }
            cambiarEstadoHonorario(h, nuevoEstado);
          }}
          className="w-40"
        >
          {opcionesEstadoDisponibles(h.estado).map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </Select>
      ),
    },
    {
      encabezado: "Cuotas",
      render: (h) => (
        <button onClick={() => verCuotas(h.id)} className="font-medium text-secundario transition-colors hover:underline">
          {cuotasHonorarioId === h.id ? "Ocultar" : "Ver cuotas"}
        </button>
      ),
    },
    {
      encabezado: "",
      render: (h) =>
        // Sin esto, un honorario sin plan de cuotas no tenía NINGÚN punto de
        // entrada en la UI para registrar un cobro — "Cobrar" sólo existía
        // por cuota (ver bloque de cuotas más abajo). El backend ya acepta
        // POST /honorarios/:id/cobrar sin cuota_id (con su propio tope de
        // saldoPendiente), este botón sólo lo expone para el caso sin cuotas.
        h.saldoPendiente != null && h.saldoPendiente > 0 ? (
          <button
            onClick={() => abrirCobroLibre(h)}
            className="font-medium text-secundario transition-colors hover:underline"
          >
            Cobrar
          </button>
        ) : null,
    },
    {
      encabezado: "",
      render: (h) => (
        <button
          onClick={() => abrirEdicionHonorario(h)}
          className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
        >
          Editar
        </button>
      ),
    },
  ];

  // --- Rendiciones ---
  const [modalRendicionAbierto, setModalRendicionAbierto] = useState(false);
  const [guardandoRendicion, setGuardandoRendicion] = useState(false);
  const [valoresRendicion, setValoresRendicion] = useState({
    total_recibido: "",
    capital_entregado: "0",
    honorarios_descontados: "0",
    gastos_descontados: "0",
    retenciones: "0",
    moneda: "ARS" as Moneda,
    fecha: new Date().toISOString().substring(0, 10),
    metodo_pago: "",
    observaciones: "",
  });
  // Resumen en vivo — sin esto, el formulario pedía 5 números por separado
  // sin mostrar nunca cómo se relacionan entre sí; el usuario no podía
  // confirmar antes de guardar que sus propios números eran consistentes
  // (ver invariante `saldo = total_recibido - suma_descuentos` en el backend).
  const totalRecibidoNum = Number(valoresRendicion.total_recibido || 0);
  const capitalEntregadoNum = Number(valoresRendicion.capital_entregado || 0);
  const honorariosDescontadosNum = Number(valoresRendicion.honorarios_descontados || 0);
  const gastosDescontadosNum = Number(valoresRendicion.gastos_descontados || 0);
  const retencionesNum = Number(valoresRendicion.retenciones || 0);
  const saldoSinDescontarRendicion =
    totalRecibidoNum - (capitalEntregadoNum + honorariosDescontadosNum + gastosDescontadosNum + retencionesNum);

  // Sin este reset, reabrir el modal después de guardar una rendición dejaba
  // los descuentos de la rendición anterior cargados — quien abriera "+ Nueva
  // Rendición" podía enviar un descuento ajeno sin darse cuenta.
  function abrirNuevaRendicion() {
    setValoresRendicion({
      total_recibido: "",
      capital_entregado: "0",
      honorarios_descontados: "0",
      gastos_descontados: "0",
      retenciones: "0",
      moneda: "ARS",
      fecha: new Date().toISOString().substring(0, 10),
      metodo_pago: "",
      observaciones: "",
    });
    setModalRendicionAbierto(true);
  }

  async function guardarRendicion(e: FormEvent) {
    e.preventDefault();
    setGuardandoRendicion(true);
    try {
      await api.post("/rendiciones", {
        expediente_id: expedienteId,
        total_recibido: Number(valoresRendicion.total_recibido),
        capital_entregado: Number(valoresRendicion.capital_entregado),
        honorarios_descontados: Number(valoresRendicion.honorarios_descontados),
        gastos_descontados: Number(valoresRendicion.gastos_descontados),
        retenciones: Number(valoresRendicion.retenciones),
        moneda: valoresRendicion.moneda,
        fecha: valoresRendicion.fecha,
        metodo_pago: valoresRendicion.metodo_pago || null,
        observaciones: valoresRendicion.observaciones || null,
      });
      mostrar("Rendición registrada.", "exito");
      setModalRendicionAbierto(false);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo registrar la rendición.", "error");
    } finally {
      setGuardandoRendicion(false);
    }
  }

  async function anularRendicion(r: Rendicion) {
    try {
      await api.patch(`/rendiciones/${r.id}/anular`);
      mostrar("Rendición anulada.", "exito");
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo anular la rendición.", "error");
    }
  }

  const columnasRendiciones: ColumnaTabla<Rendicion>[] = [
    { encabezado: "Fecha", render: (r) => r.fecha },
    { encabezado: "Total Recibido", render: (r) => `$${r.total_recibido.toLocaleString("es-AR")} ${r.moneda}` },
    { encabezado: "Capital Entregado", render: (r) => `$${r.capital_entregado.toLocaleString("es-AR")}` },
    { encabezado: "Honorarios", render: (r) => `$${r.honorarios_descontados.toLocaleString("es-AR")}` },
    { encabezado: "Saldo", render: (r) => `$${r.saldo.toLocaleString("es-AR")}` },
    { encabezado: "Estado", render: (r) => <Insignia texto={r.estado} /> },
    {
      encabezado: "Acciones",
      render: (r) =>
        r.estado === "Cerrada" ? (
          <button onClick={() => anularRendicion(r)} className="font-medium text-error transition-colors hover:underline">
            Anular
          </button>
        ) : (
          "—"
        ),
    },
  ];

  // --- Movimientos (ledger del expediente) ---
  const [modalMovimientoAbierto, setModalMovimientoAbierto] = useState(false);
  const [guardandoMovimiento, setGuardandoMovimiento] = useState(false);
  const [valoresMovimiento, setValoresMovimiento] = useState({
    tipo: "Capital del Cliente" as TipoMovimientoEconomico,
    concepto: "",
    fecha: new Date().toISOString().substring(0, 10),
    importe: "",
    moneda: "ARS" as Moneda,
    categoria_id: "",
    subcategoria_id: "",
    honorario_id: "",
  });

  async function guardarMovimiento(e: FormEvent) {
    e.preventDefault();
    setGuardandoMovimiento(true);
    try {
      await api.post("/economia", {
        ...valoresMovimiento,
        importe: Number(valoresMovimiento.importe),
        categoria_id: valoresMovimiento.categoria_id || null,
        subcategoria_id: valoresMovimiento.subcategoria_id || null,
        // honorario_id sólo tiene sentido (y el backend sólo lo acepta)
        // para "Pago a Profesional" — es la única forma de que ese pago
        // quede atribuido a un profesional concreto (vía honorario.
        // profesional_beneficiario_id) sin inventar un campo nuevo.
        honorario_id: valoresMovimiento.tipo === "Pago a Profesional" ? valoresMovimiento.honorario_id || null : null,
        expediente_id: expedienteId,
        cliente_id: clienteId,
      });
      mostrar("Movimiento registrado.", "exito");
      setModalMovimientoAbierto(false);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo registrar el movimiento.", "error");
    } finally {
      setGuardandoMovimiento(false);
    }
  }

  const columnasMovimientos: ColumnaTabla<MovimientoEconomico>[] = [
    { encabezado: "Fecha", render: (m) => m.fecha },
    { encabezado: "Tipo", render: (m) => m.tipo },
    { encabezado: "Categoría", render: (m) => m.categoria_nombre ?? "—" },
    { encabezado: "Subcategoría", render: (m) => m.subcategoria_nombre ?? "—" },
    { encabezado: "Concepto", render: (m) => m.concepto },
    { encabezado: "Profesional", render: (m) => m.profesional_beneficiario_nombre ?? "—" },
    { encabezado: "Importe", render: (m) => `$${m.importe.toLocaleString("es-AR")} ${m.moneda}` },
  ];

  return (
    <div className="space-y-8">
      <Tarjeta>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-texto">Honorarios</h3>
          <div className="flex gap-2">
            <Boton tamano="sm" variante="secundario" onClick={() => setPresupuestadorAbierto(true)}>
              Presupuestador
            </Boton>
            <Boton tamano="sm" onClick={abrirNuevoHonorario}>
              + Nuevo Honorario
            </Boton>
          </div>
        </div>
        <Tabla
          columnas={columnasHonorarios}
          filas={honorarios}
          claveFila={(h) => h.id}
          tituloVacio="Sin honorarios registrados"
        />
        {cuotasHonorarioId && (
          <div className="mt-3 rounded-lg border border-borde bg-fondo p-3">
            {cargandoCuotas ? (
              <p className="text-sm text-texto-secundario">Cargando cuotas…</p>
            ) : cuotas.length === 0 ? (
              <p className="text-sm text-texto-secundario">Este honorario no tiene un plan de cuotas.</p>
            ) : (
              <>
                <table className="w-full text-left text-sm">
                  <thead className="text-texto-secundario">
                    <tr>
                      <th className="pb-1">Cuota</th>
                      <th className="pb-1">Vencimiento</th>
                      <th className="pb-1">Importe</th>
                      <th className="pb-1">Estado</th>
                      <th className="pb-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuotas.map((cu) => (
                      <tr key={cu.id} className="border-t border-borde">
                        <td className="py-1">{cu.numero}</td>
                        <td className="py-1">{cu.vencimiento}</td>
                        <td className="py-1">${cu.importe_total.toLocaleString("es-AR")}</td>
                        <td className="py-1">
                          <Insignia texto={cu.estado} />
                        </td>
                        <td className="py-1">
                          {cu.estado === "Pendiente" && (
                            <button
                              onClick={() => cobrarCuota(cuotasHonorarioId, cu)}
                              className="font-medium text-secundario transition-colors hover:underline"
                            >
                              Cobrar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!cuotas.some((cu) => cu.estado === "Pagada") && (
                  <div className="mt-2 flex justify-end">
                    <Boton tamano="sm" variante="secundario" onClick={abrirEditarCuotas}>
                      Editar cuotas
                    </Boton>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Tarjeta>

      <Tarjeta>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-texto">Rendiciones</h3>
          <Boton tamano="sm" onClick={abrirNuevaRendicion}>
            + Nueva Rendición
          </Boton>
        </div>
        <Tabla
          columnas={columnasRendiciones}
          filas={rendiciones}
          claveFila={(r) => r.id}
          tituloVacio="Sin rendiciones registradas"
        />
      </Tarjeta>

      <Tarjeta>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-texto">Movimientos del expediente</h3>
          <Boton tamano="sm" onClick={() => setModalMovimientoAbierto(true)}>
            + Nuevo Movimiento
          </Boton>
        </div>
        <Tabla
          columnas={columnasMovimientos}
          filas={movimientos}
          claveFila={(m) => m.id}
          tituloVacio="Sin movimientos registrados"
        />
      </Tarjeta>

      <Modal
        titulo={enEdicionHonorario ? "Editar honorario" : "Nuevo honorario"}
        abierto={modalHonorarioAbierto}
        onCerrar={() => setModalHonorarioAbierto(false)}
        ancho="max-w-xl"
      >
        <form onSubmit={guardarHonorario} className="space-y-6">
          {tieneCobro && (
            <p className="rounded-lg border border-borde bg-fondo p-3 text-xs text-texto-secundario">
              Este honorario ya tiene cobros registrados: los montos y el descuento no se pueden modificar. El resto
              de los datos sigue siendo editable.
            </p>
          )}

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-texto">Información general</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo etiqueta="Tipo de honorario">
                <SelectorTipoHonorario
                  valor={valoresHonorario.tipo_honorario}
                  onCambiar={(v) => setValoresHonorario((val) => ({ ...val, tipo_honorario: v }))}
                />
              </Campo>
              <Campo etiqueta="Modalidad" obligatorio>
                <Select
                  value={valoresHonorario.modalidad}
                  disabled={campoProtegido("modalidad")}
                  onChange={(e) =>
                    setValoresHonorario((v) => ({ ...v, modalidad: e.target.value as ModalidadHonorario }))
                  }
                >
                  {MODALIDADES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </Campo>
              <Campo etiqueta="Moneda">
                <Select
                  value={valoresHonorario.moneda}
                  disabled={campoProtegido("moneda")}
                  onChange={(e) => setValoresHonorario((v) => ({ ...v, moneda: e.target.value as Moneda }))}
                >
                  {MONEDAS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </Campo>
              <Campo etiqueta="Profesional beneficiario">
                <Select
                  value={valoresHonorario.profesional_beneficiario_id}
                  onChange={(e) =>
                    setValoresHonorario((v) => ({ ...v, profesional_beneficiario_id: e.target.value }))
                  }
                >
                  <option value="">Sin asignar</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre_completo}
                    </option>
                  ))}
                </Select>
              </Campo>
            </div>
          </div>

          <div className="space-y-3 border-t border-borde pt-4">
            <h4 className="text-sm font-semibold text-texto">Valor</h4>
            {valoresHonorario.modalidad === "Monto Fijo" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo etiqueta="Importe fijo" error={errorImporteFijo}>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={campoProtegido("importe_fijo")}
                    value={valoresHonorario.importe_fijo}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, importe_fijo: e.target.value }))}
                  />
                </Campo>
              </div>
            )}
            {valoresHonorario.modalidad === "Porcentaje" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo etiqueta="Porcentaje (%)" error={errorPorcentaje}>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={campoProtegido("porcentaje")}
                    value={valoresHonorario.porcentaje}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, porcentaje: e.target.value }))}
                  />
                </Campo>
                <Campo etiqueta="Base de cálculo (opcional)">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={campoProtegido("base_calculo")}
                    value={valoresHonorario.base_calculo}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, base_calculo: e.target.value }))}
                    placeholder="Monto sobre el que aplica el %"
                  />
                </Campo>
              </div>
            )}
            {(valoresHonorario.modalidad === "Mixto" ||
              valoresHonorario.modalidad === "Regulado Judicialmente" ||
              valoresHonorario.modalidad === "Extrajudicial") && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo etiqueta="Porcentaje (%)">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={campoProtegido("porcentaje")}
                    value={valoresHonorario.porcentaje}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, porcentaje: e.target.value }))}
                  />
                </Campo>
                <Campo etiqueta="Importe fijo">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={campoProtegido("importe_fijo")}
                    value={valoresHonorario.importe_fijo}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, importe_fijo: e.target.value }))}
                  />
                </Campo>
              </div>
            )}

            {valoresHonorario.modalidad === "Monto Fijo" && (
              <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
                <p className="mb-2 text-xs font-medium text-texto-secundario">Calcular desde cantidad de JUS</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end">
                  <Campo etiqueta="Cantidad de JUS">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={cantidadJus}
                      onChange={(e) => setCantidadJus(e.target.value)}
                    />
                  </Campo>
                  <div>
                    <p className="mb-1 text-sm font-medium text-texto">Valor JUS vigente</p>
                    <p className="text-sm text-texto-secundario">
                      {valorJusVigente != null ? `$${valorJusVigente.toLocaleString("es-AR")}` : "No disponible"}
                    </p>
                  </div>
                  <Boton type="button" tamano="sm" variante="secundario" onClick={aplicarCalculoJus} disabled={totalJusCalculado == null}>
                    Usar ${totalJusCalculado?.toLocaleString("es-AR") ?? "0"}
                  </Boton>
                </div>
              </div>
            )}
            {valoresHonorario.modalidad === "Porcentaje" && totalPorcentajeCalculado != null && (
              <div className="flex items-center justify-between rounded-lg border border-borde bg-fondo p-3 text-sm">
                <span>
                  Total calculado: <strong>${totalPorcentajeCalculado.toLocaleString("es-AR")}</strong>
                </span>
                <Boton type="button" tamano="sm" variante="secundario" onClick={aplicarBaseCalculo}>
                  Usar este total
                </Boton>
              </div>
            )}
            {valoresHonorario.modalidad === "Monto Fijo" &&
              totalMontoFijoCalculado != null &&
              montoActivoNum !== totalMontoFijoCalculado && (
                <div className="flex items-center justify-between rounded-lg border border-borde bg-fondo p-3 text-sm">
                  <span>
                    Importe fijo cargado: <strong>${totalMontoFijoCalculado.toLocaleString("es-AR")}</strong>
                  </span>
                  <Boton type="button" tamano="sm" variante="secundario" onClick={aplicarMontoFijoComoActivo}>
                    Usar este importe
                  </Boton>
                </div>
              )}

            <Campo etiqueta={LABEL_MONTO_ACTIVO[campoActivo]}>
              <Input
                type="number"
                min="0"
                step="0.01"
                disabled={campoProtegido(campoActivo)}
                value={valoresHonorario[campoActivo]}
                onChange={(e) => setValoresHonorario((v) => ({ ...v, [campoActivo]: e.target.value }))}
              />
            </Campo>
          </div>

          <div className="space-y-3 border-t border-borde pt-4">
            <h4 className="text-sm font-semibold text-texto">Condiciones</h4>
            <div>
              <p className="mb-1 text-sm font-medium text-texto">¿Aplicar descuento?</p>
              <AlternativaBinaria
                valor={Boolean(valoresHonorario.descuento_tipo)}
                disabled={campoProtegido("descuento_tipo")}
                onCambiar={(aplica) =>
                  setValoresHonorario((v) => ({
                    ...v,
                    descuento_tipo: aplica ? (v.descuento_tipo || "Porcentaje") : "",
                    descuento_valor: aplica ? v.descuento_valor : "",
                    motivo_descuento: aplica ? v.motivo_descuento : "",
                  }))
                }
              />
            </div>
            {valoresHonorario.descuento_tipo && (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Campo etiqueta="Tipo de descuento">
                    <Select
                      value={valoresHonorario.descuento_tipo}
                      disabled={campoProtegido("descuento_tipo")}
                      onChange={(e) =>
                        setValoresHonorario((v) => ({
                          ...v,
                          descuento_tipo: e.target.value as TipoDescuentoHonorario,
                        }))
                      }
                    >
                      <option value="Porcentaje">Porcentaje</option>
                      <option value="Importe">Importe</option>
                    </Select>
                  </Campo>
                  <Campo etiqueta="Valor del descuento" error={errorDescuentoMonto}>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={campoProtegido("descuento_valor")}
                      value={valoresHonorario.descuento_valor}
                      onChange={(e) => setValoresHonorario((v) => ({ ...v, descuento_valor: e.target.value }))}
                    />
                  </Campo>
                </div>
                <Campo etiqueta="Motivo del descuento" obligatorio={descuentoSuperaVeinte} error={errorMotivoDescuento}>
                  <TextArea
                    rows={2}
                    disabled={campoProtegido("motivo_descuento")}
                    value={valoresHonorario.motivo_descuento}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, motivo_descuento: e.target.value }))}
                  />
                </Campo>
              </>
            )}

            {mostrarFechasPorEstado(estadoActivo) && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo etiqueta="Fecha de envío">
                  <Input
                    type="date"
                    value={valoresHonorario.fecha_envio}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, fecha_envio: e.target.value }))}
                  />
                </Campo>
                <Campo etiqueta="Válido hasta">
                  <Input
                    type="date"
                    value={valoresHonorario.validez_hasta}
                    onChange={(e) => setValoresHonorario((v) => ({ ...v, validez_hasta: e.target.value }))}
                  />
                </Campo>
              </div>
            )}
          </div>

          <div className="border-t border-borde pt-4">
            <Campo etiqueta="Observaciones">
              <TextArea
                rows={2}
                value={valoresHonorario.observaciones}
                onChange={(e) => setValoresHonorario((v) => ({ ...v, observaciones: e.target.value }))}
              />
            </Campo>
          </div>

          <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
            <p className="mb-1 font-medium text-texto">Resumen</p>
            <p>Tipo: {valoresHonorario.tipo_honorario || "—"}</p>
            <p>Modalidad: {valoresHonorario.modalidad}</p>
            <p>
              {LABEL_MONTO_ACTIVO[campoActivo]}: ${montoActivoNum.toLocaleString("es-AR")} {valoresHonorario.moneda}
            </p>
            <p>
              Descuento:{" "}
              {valoresHonorario.descuento_tipo ? `$${montoDescuentoLocal.toLocaleString("es-AR")}` : "Sin descuento"}
            </p>
            <p className="font-semibold text-texto">Total final: ${totalFinalLocal.toLocaleString("es-AR")}</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalHonorarioAbierto(false)}
              disabled={guardandoHonorario}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoHonorario} disabled={hayErroresHonorario}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal titulo="Editar plan de cuotas" abierto={editarCuotasAbierto} onCerrar={() => setEditarCuotasAbierto(false)}>
        <form onSubmit={guardarPlanCuotas} className="space-y-4">
          <p className="text-xs text-texto-secundario">
            Se reemplaza el plan de cuotas completo. Solo disponible si ninguna cuota fue cobrada.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Entrega inicial (anticipo)">
              <Input
                type="number"
                value={planCuotas.anticipo}
                onChange={(e) => setPlanCuotas((v) => ({ ...v, anticipo: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Cantidad de cuotas" obligatorio>
              <Input
                type="number"
                min={1}
                required
                value={planCuotas.cantidadCuotas}
                onChange={(e) => setPlanCuotas((v) => ({ ...v, cantidadCuotas: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Frecuencia" obligatorio>
              <Select
                value={planCuotas.frecuencia}
                onChange={(e) => setPlanCuotas((v) => ({ ...v, frecuencia: e.target.value as FrecuenciaCuotas }))}
              >
                {FRECUENCIAS_CUOTAS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Primer vencimiento" obligatorio>
              <Input
                type="date"
                required
                value={planCuotas.primeraFecha}
                onChange={(e) => setPlanCuotas((v) => ({ ...v, primeraFecha: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Interés por cuota (%)">
              <Input
                type="number"
                value={planCuotas.interesPctPorCuota}
                onChange={(e) => setPlanCuotas((v) => ({ ...v, interesPctPorCuota: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setEditarCuotasAbierto(false)}
              disabled={guardandoCuotas}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoCuotas}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Registrar cobro"
        abierto={Boolean(cobroLibreHonorario)}
        onCerrar={() => setCobroLibreHonorario(null)}
      >
        {cobroLibreHonorario && (
          <form onSubmit={guardarCobroLibre} className="space-y-4">
            <p className="text-sm text-texto-secundario">
              Saldo pendiente actual:{" "}
              <strong>
                ${(cobroLibreHonorario.saldoPendiente ?? 0).toLocaleString("es-AR")} {cobroLibreHonorario.moneda}
              </strong>
            </p>
            <Campo etiqueta="Importe cobrado" obligatorio>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={valoresCobroLibre.importe}
                onChange={(e) => setValoresCobroLibre((v) => ({ ...v, importe: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Fecha" obligatorio>
              <Input
                type="date"
                required
                value={valoresCobroLibre.fecha}
                onChange={(e) => setValoresCobroLibre((v) => ({ ...v, fecha: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Concepto (opcional)">
              <Input
                type="text"
                value={valoresCobroLibre.concepto}
                onChange={(e) => setValoresCobroLibre((v) => ({ ...v, concepto: e.target.value }))}
              />
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Boton
                type="button"
                variante="secundario"
                onClick={() => setCobroLibreHonorario(null)}
                disabled={guardandoCobroLibre}
              >
                Cancelar
              </Boton>
              <Boton type="submit" cargando={guardandoCobroLibre}>
                Registrar cobro
              </Boton>
            </div>
          </form>
        )}
      </Modal>

      <Modal titulo="Nueva rendición" abierto={modalRendicionAbierto} onCerrar={() => setModalRendicionAbierto(false)}>
        <form onSubmit={guardarRendicion} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Total recibido" obligatorio>
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                value={valoresRendicion.total_recibido}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, total_recibido: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Fecha" obligatorio>
              <Input
                type="date"
                required
                value={valoresRendicion.fecha}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, fecha: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Capital entregado">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={valoresRendicion.capital_entregado}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, capital_entregado: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Honorarios descontados">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={valoresRendicion.honorarios_descontados}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, honorarios_descontados: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Gastos descontados">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={valoresRendicion.gastos_descontados}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, gastos_descontados: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Retenciones">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={valoresRendicion.retenciones}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, retenciones: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Moneda">
              <Select
                value={valoresRendicion.moneda}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, moneda: e.target.value as Moneda }))}
              >
                {MONEDAS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Método de pago">
              <Input
                value={valoresRendicion.metodo_pago}
                onChange={(e) => setValoresRendicion((v) => ({ ...v, metodo_pago: e.target.value }))}
              />
            </Campo>
          </div>
          <Campo etiqueta="Observaciones">
            <TextArea
              rows={2}
              value={valoresRendicion.observaciones}
              onChange={(e) => setValoresRendicion((v) => ({ ...v, observaciones: e.target.value }))}
            />
          </Campo>

          <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
            <p className="mb-1 font-medium text-texto">Resumen</p>
            <p>
              Entró: ${totalRecibidoNum.toLocaleString("es-AR")} {valoresRendicion.moneda}
            </p>
            <p>Honorarios descontados: ${honorariosDescontadosNum.toLocaleString("es-AR")}</p>
            <p>Gastos descontados: ${gastosDescontadosNum.toLocaleString("es-AR")}</p>
            <p>Retención: ${retencionesNum.toLocaleString("es-AR")}</p>
            <p>Entregado al cliente: ${capitalEntregadoNum.toLocaleString("es-AR")}</p>
            <p
              className={`font-semibold ${saldoSinDescontarRendicion < 0 ? "text-error" : "text-texto"}`}
            >
              Saldo sin descontar: ${saldoSinDescontarRendicion.toLocaleString("es-AR")}
            </p>
            {saldoSinDescontarRendicion < 0 && (
              <p className="mt-1 text-xs text-error">
                Los descuentos superan el total recibido — el guardado se va a rechazar.
              </p>
            )}
            {saldoSinDescontarRendicion > 0 && (
              <p className="mt-1 text-xs text-texto-secundario">
                Este monto queda sin categorizar en la rendición (no es necesariamente un error).
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalRendicionAbierto(false)}
              disabled={guardandoRendicion}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoRendicion}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Nuevo movimiento"
        abierto={modalMovimientoAbierto}
        onCerrar={() => setModalMovimientoAbierto(false)}
      >
        <form onSubmit={guardarMovimiento} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Tipo" obligatorio>
              <Select
                value={valoresMovimiento.tipo}
                onChange={(e) =>
                  setValoresMovimiento((v) => ({
                    ...v,
                    tipo: e.target.value as TipoMovimientoEconomico,
                    categoria_id: "",
                    subcategoria_id: "",
                    // honorario_id sólo es válido para "Pago a Profesional" —
                    // si se cambia a otro tipo, se descarta para no arrastrar
                    // un vínculo que el backend rechazaría igual.
                    honorario_id: "",
                  }))
                }
              >
                {TIPOS_MOVIMIENTO_SELECCIONABLES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {valoresMovimiento.tipo === "Retención" && (
                <p className="mt-1 text-xs text-texto-secundario">
                  Este tipo es informativo: no se suma al Saldo Cliente ni al Saldo Estudio.
                </p>
              )}
            </Campo>
            <Campo etiqueta="Fecha" obligatorio>
              <Input
                type="date"
                required
                value={valoresMovimiento.fecha}
                onChange={(e) => setValoresMovimiento((v) => ({ ...v, fecha: e.target.value }))}
              />
            </Campo>
          </div>
          <SelectorCategoriaEconomica
            naturaleza={NATURALEZA_CATEGORIA[valoresMovimiento.tipo] ?? null}
            categoriaId={valoresMovimiento.categoria_id}
            subcategoriaId={valoresMovimiento.subcategoria_id}
            onCambiarCategoria={(id) => setValoresMovimiento((v) => ({ ...v, categoria_id: id }))}
            onCambiarSubcategoria={(id) => setValoresMovimiento((v) => ({ ...v, subcategoria_id: id }))}
          />
          {valoresMovimiento.tipo === "Pago a Profesional" && honorarios.length > 0 && (
            <Campo etiqueta="Honorario (opcional)">
              <Select
                value={valoresMovimiento.honorario_id}
                onChange={(e) => setValoresMovimiento((v) => ({ ...v, honorario_id: e.target.value }))}
              >
                <option value="">Sin vincular a un honorario</option>
                {honorarios.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.tipo_honorario ?? h.modalidad} — {h.moneda}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-texto-secundario">
                Si este pago corresponde a un Honorario de este expediente, vincularlo permite saber a qué
                profesional corresponde. Un pago a un profesional externo (sin Honorario registrado) puede dejarse
                sin vincular.
              </p>
            </Campo>
          )}
          <Campo etiqueta="Concepto" obligatorio>
            <Input
              required
              value={valoresMovimiento.concepto}
              onChange={(e) => setValoresMovimiento((v) => ({ ...v, concepto: e.target.value }))}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Importe" obligatorio>
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                value={valoresMovimiento.importe}
                onChange={(e) => setValoresMovimiento((v) => ({ ...v, importe: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Moneda">
              <Select
                value={valoresMovimiento.moneda}
                onChange={(e) => setValoresMovimiento((v) => ({ ...v, moneda: e.target.value as Moneda }))}
              >
                {MONEDAS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Campo>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalMovimientoAbierto(false)}
              disabled={guardandoMovimiento}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoMovimiento}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <PresupuestadorHonorarios
        abierto={presupuestadorAbierto}
        onCerrar={() => setPresupuestadorAbierto(false)}
        clienteIdInicial={clienteId}
        expedienteIdInicial={expedienteId}
        onCreado={onCambio}
      />

      <ConfirmacionDialog
        abierto={confirmandoAnulacionHonorario != null}
        titulo="Anular honorario"
        mensaje={`Vas a anular el honorario "${confirmandoAnulacionHonorario?.tipo_honorario ?? confirmandoAnulacionHonorario?.modalidad ?? ""}". Esta acción no se puede deshacer: un honorario Anulado no puede volver a ningún estado anterior.`}
        textoConfirmar="Anular"
        peligroso
        onConfirmar={() => {
          if (confirmandoAnulacionHonorario) {
            cambiarEstadoHonorario(confirmandoAnulacionHonorario, "Anulado");
          }
          setConfirmandoAnulacionHonorario(null);
        }}
        onCancelar={() => setConfirmandoAnulacionHonorario(null)}
      />
    </div>
  );
}
